'use client';
import { useEffect, useRef, useState } from 'react';
import type { DesktopAction } from 'elowen-plugin-ui-kit';

/** How long a finished action stays on the glass, and the least time any shown action is left alone before a
 *  newer one may replace it. A `started` action has no limit of its own: it stays until its `done`/`failed`. */
const MARKER_LINGER_MS = { done: 2_000, failed: 5_000 } as const;
const MARKER_MIN_MS = 400;
/** The guest composites the same ring into live frames for 480 ms; the still gets a matching one. */
const RING_MS = 480;

type Marker = { key: string; status: NonNullable<DesktopAction['status']>; text: string; receivedAt: number };

/** One operation is one key: its `started` and `done` share an id. An event without an id (Browser's) is its own
 *  action, told apart by when it arrived. */
const actionKey = (action: DesktopAction): string => action.id ?? `@${action.receivedAt}`;

function clearTimer(timer: { current: ReturnType<typeof setTimeout> | null }): void {
  if (timer.current) { clearTimeout(timer.current); timer.current = null; }
}

/** A finished action is over once its lifetime, counted from the moment the event arrived, has passed. */
const lapsed = (marker: Marker, now: number): boolean =>
  marker.status !== 'started' && now - marker.receivedAt >= MARKER_LINGER_MS[marker.status];

/** The line the glass shows for the agent's current action. The producer composes `text` from `action`
 *  immediately; this decides how long it is visible. An action that has not finished stays until it does, a
 *  done one for two seconds and a failed one for five, counted from the event's `receivedAt` (so a re-render,
 *  a late mount or a new text for the same event never restarts the time), and a newer action replaces the
 *  shown one only after it has been up for 400 ms (the newest waiting action wins). An event without a status
 *  counts as done at receipt. The same operation's own `done` updates the shown line at once. Without an
 *  action the text is not this hook's to time and the caller passes it through. */
export function useDesktopActionMarker(action: DesktopAction | null, text: string | null | undefined): Marker | null {
  const [shown, setShown] = useState<Marker | null>(null);
  const current = useRef<Marker | null>(null);
  const shownAt = useRef(0);
  const pending = useRef<Marker | null>(null);
  const hold = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiry = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { clearTimer(hold); clearTimer(expiry); }, []);
  useEffect(() => {
    const show = (marker: Marker, sameAction: boolean): void => {
      if (!sameAction) shownAt.current = Date.now();
      current.current = marker;
      setShown(marker);
      clearTimer(expiry);
      if (marker.status === 'started') return;
      const left = MARKER_LINGER_MS[marker.status] - (Date.now() - marker.receivedAt);
      expiry.current = setTimeout(() => { expiry.current = null; current.current = null; setShown(null); }, left);
    };
    const flush = (): void => {
      clearTimer(hold);
      const next = pending.current;
      pending.current = null;
      // One that ran out while it waited for its turn is simply never shown.
      if (next && !lapsed(next, Date.now())) show(next, false);
    };
    if (!action || !text) {
      clearTimer(hold); clearTimer(expiry);
      pending.current = null; current.current = null;
      setShown(null);
      return;
    }
    const marker: Marker = { key: actionKey(action), status: action.status ?? 'done', text, receivedAt: action.receivedAt };
    if (lapsed(marker, Date.now())) {
      // Over before this tile saw it, or by the time the same event was drawn again: nothing is left to show.
      if (current.current?.key === marker.key) { clearTimer(expiry); current.current = null; setShown(null); }
      return;
    }
    if (!current.current || current.current.key === marker.key) { show(marker, !!current.current); return; }
    pending.current = marker;
    const wait = shownAt.current + MARKER_MIN_MS - Date.now();
    if (wait <= 0) flush();
    else if (!hold.current) hold.current = setTimeout(flush, wait);
  }, [action, text]);
  return shown;
}

/** The ring the still draws where the agent's input landed. One per action, at the first event that carries a
 *  point: a labelled click learns its point only at `done`, a coordinate click has it from `started`. A failed
 *  action puts the ring nowhere, because nothing landed, and takes away the one its `started` drew. */
export function useDesktopActionRing(action: DesktopAction | null): { key: string; x: number; y: number } | null {
  const [ring, setRing] = useState<{ key: string; x: number; y: number } | null>(null);
  const rung = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => clearTimer(timer), []);
  useEffect(() => {
    if (!action) return;
    const key = actionKey(action);
    if (action.status === 'failed') {
      // Nothing landed: the ring this action already drew from its `started` goes with it.
      if (rung.current === key) { clearTimer(timer); setRing(null); }
      return;
    }
    if (!action.point || rung.current === key) return;
    rung.current = key;
    const left = RING_MS - (Date.now() - action.receivedAt);
    if (left <= 0) return;
    clearTimer(timer);
    setRing({ key, ...action.point });
    timer.current = setTimeout(() => { timer.current = null; setRing(null); }, left);
  }, [action]);
  return ring;
}
